import React, { useState, useEffect } from 'react';

// ── Layout constants ──────────────────────────────────────────────────────────
const R      = 18;
const SVG_W  = 680;
const SVG_H  = 290;
const BAND_Y = 34; // top of zone bands (below zone labels)

// ── Zone definitions (x, w must sum to SVG_W) ────────────────────────────────
const ZONES = [
  { id: 'internet', label: 'INTERNET',         x: 0,   w: 140, fill: '#ef444406' },
  { id: 'corp',     label: 'CORPORATE IT',      x: 140, w: 255, fill: '#f59e0b04' },
  { id: 'dmz',      label: 'DMZ',               x: 395, w: 90,  fill: '#8b5cf606' },
  { id: 'ot',       label: 'OT CONTROL ZONE',   x: 485, w: 195, fill: '#22c55e06' },
];

// ── Node definitions ──────────────────────────────────────────────────────────
// Each cx must fall inside the matching zone's x..x+w range.
// Zone centers: internet=70, corp=267, dmz=440, ot=582
const NODE_DEFS = [
  { id: 'atk',      label: 'ATTACKER',   type: 'attacker',    cx: 70,  cy: 165 },
  { id: 'edge-fw',  label: 'EDGE-FW',    type: 'firewall',    cx: 192, cy: 165 },
  { id: 'corp-fw',  label: 'CORP-FW',    type: 'firewall',    cx: 232, cy: 78  },
  { id: 'eng-ws',   label: 'ENG-WS-04',  type: 'workstation', cx: 310, cy: 165 },
  { id: 'it-srv',   label: 'IT-SRV',     type: 'server',      cx: 310, cy: 250 },
  { id: 'it-ot-fw', label: 'IT-OT FW',   type: 'firewall',    cx: 440, cy: 165 },
  { id: 'scada',    label: 'SCADA-01',   type: 'server',      cx: 578, cy: 98  },
  { id: 'hmi',      label: 'HMI-01',     type: 'monitor',     cx: 578, cy: 165 },
  { id: 'plc',      label: 'PLC-01',     type: 'server',      cx: 578, cy: 232 },
];

// ── Edge definitions ──────────────────────────────────────────────────────────
const EDGES = [
  // Background connections (not on the attack path)
  { from: 'edge-fw',  to: 'corp-fw', hot: false },
  { from: 'eng-ws',   to: 'it-srv',  hot: false },
  // Attack path — animated phase by phase
  { from: 'atk',      to: 'edge-fw',  hot: true, phase: 1 },
  { from: 'edge-fw',  to: 'eng-ws',   hot: true, phase: 2 },
  { from: 'eng-ws',   to: 'it-ot-fw', hot: true, phase: 3 },
  { from: 'it-ot-fw', to: 'scada',    hot: true, phase: 4 },
  { from: 'it-ot-fw', to: 'hmi',      hot: true, phase: 4 },
  { from: 'it-ot-fw', to: 'plc',      hot: true, phase: 4 },
];

// ── Animation timing (ms) ─────────────────────────────────────────────────────
// Total runtime ≈ 8 s — paced for a first-time observer to read each phase.
//   0.0–0.8 s  : hold — read zone labels and initial topology
//   0.8–2.0 s  : Phase 1 — probe the boundary (ATK → EDGE-FW)
//   2.0–3.5 s  : Phase 2 — entry point found (EDGE-FW → ENG-WS); node turns red
//   3.5–5.2 s  : Phase 3 — lateral move into DMZ (ENG-WS → IT-OT FW); FW turns amber
//   5.2–7.0 s  : Phase 4 — pivot into OT zone (fan-out); OT nodes flash amber
//   7.0–8.0 s  : hold — digest final compromised state
const PHASE = {
  1: { delay: 600,  dur: 1100 },
  2: { delay: 1700, dur: 1300 },
  3: { delay: 3000, dur: 1500 },
  4: { delay: 4500, dur: 1600 },
};

// Node state → stroke/fill/label color
const STATE_COLOR = {
  attacker:    '#ef4444',
  clean:       '#475569',
  compromised: '#ef4444',
  threatened:  '#f59e0b',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function nodeById(id) { return NODE_DEFS.find(n => n.id === id); }

function edgeLen(n1, n2) {
  return Math.ceil(Math.sqrt((n2.cx - n1.cx) ** 2 + (n2.cy - n1.cy) ** 2));
}

// Stable CSS keyframe name: replace hyphens so the identifier is unambiguous
function animName(from, to) {
  return `draw_${from}_${to}`.replace(/-/g, '_');
}

// ── NodeIcon — same visual vocabulary as InteractiveTerminal ─────────────────
function NodeIcon({ type, r, color }) {
  const s = r * 0.55;
  switch (type) {
    case 'firewall':
      return (
        <path
          d={`M0,${-s} L${s},${-s * 0.4} L${s},${s * 0.1} Q${s},${s * 0.75} 0,${s} Q${-s},${s * 0.75} ${-s},${s * 0.1} L${-s},${-s * 0.4} Z`}
          fill="none" stroke={color} strokeWidth="1.3"
        />
      );
    case 'server':
      return (
        <g>
          {[-0.55, -0.1, 0.35].map((dy, i) => (
            <rect key={i} x={-s} y={dy * s} width={s * 2} height={s * 0.42}
              rx="1" fill="none" stroke={color} strokeWidth="1.1" />
          ))}
        </g>
      );
    case 'workstation':
      return (
        <g>
          <rect x={-s} y={-s * 0.7} width={s * 2} height={s * 1.1}
            rx="1.5" fill="none" stroke={color} strokeWidth="1.1" />
          <line x1={-s * 0.5} y1={s * 0.55} x2={s * 0.5} y2={s * 0.55}
            stroke={color} strokeWidth="1.1" />
        </g>
      );
    case 'monitor':
      return (
        <g>
          <rect x={-s * 0.95} y={-s * 0.65} width={s * 1.9} height={s * 1.05}
            rx="1.5" fill="none" stroke={color} strokeWidth="1.1" />
          <line x1="0" y1={s * 0.4} x2="0" y2={s * 0.65} stroke={color} strokeWidth="1.1" />
          <line x1={-s * 0.4} y1={s * 0.65} x2={s * 0.4} y2={s * 0.65}
            stroke={color} strokeWidth="1.1" />
        </g>
      );
    case 'attacker':
    default:
      // Warning triangle with exclamation mark
      return (
        <g>
          <path d={`M0,${-s * 0.85} L${s * 0.9},${s * 0.55} L${-s * 0.9},${s * 0.55} Z`}
            fill="none" stroke={color} strokeWidth="1.4" />
          <line x1="0" y1={-s * 0.25} x2="0" y2={s * 0.15} stroke={color} strokeWidth="1.5" />
          <circle cx="0" cy={s * 0.38} r={s * 0.13} fill={color} />
        </g>
      );
  }
}

// ── Main component ────────────────────────────────────────────────────────────
export default function IntrusionTopology() {
  const [states, setStates] = useState(() =>
    Object.fromEntries(NODE_DEFS.map(n => [n.id, n.id === 'atk' ? 'attacker' : 'clean']))
  );

  useEffect(() => {
    const timers = [
      // Phase 2 completes: entry point turns red
      setTimeout(() => setStates(s => ({ ...s, 'eng-ws': 'compromised' })), 3000),
      // Phase 3 completes: IT-OT firewall turns amber (breached)
      setTimeout(() => setStates(s => ({ ...s, 'it-ot-fw': 'threatened' })), 4500),
      // Phase 4 completes: OT assets turn amber (threatened)
      setTimeout(() => setStates(s => ({ ...s, scada: 'threatened', hmi: 'threatened', plc: 'threatened' })), 6100),
    ];
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hotEdges = EDGES.filter(e => e.hot);

  // Build CSS keyframes for each animated edge (one per unique from→to pair)
  const keyframeCss = hotEdges.map(e => {
    const n1 = nodeById(e.from);
    const n2 = nodeById(e.to);
    const len = edgeLen(n1, n2);
    return `@keyframes ${animName(e.from, e.to)} { from { stroke-dashoffset: ${len}; } to { stroke-dashoffset: 0; } }`;
  }).join(' ');

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      width="100%"
      style={{ display: 'block' }}
      aria-label="Network intrusion path visualisation"
    >
      <defs><style>{keyframeCss}</style></defs>

      {/* ── Zone bands and headers ─────────────────────────────────────────── */}
      {ZONES.map(z => (
        <g key={z.id}>
          <rect x={z.x} y={BAND_Y} width={z.w} height={SVG_H - BAND_Y} fill={z.fill} />
          {/* Separator line (skip the leftmost zone) */}
          {z.x > 0 && (
            <line x1={z.x} y1={BAND_Y} x2={z.x} y2={SVG_H}
              stroke="#334155" strokeWidth="1" strokeDasharray="3 3" />
          )}
          {/* Zone label — centred over the band */}
          <text
            x={z.x + z.w / 2} y={22}
            textAnchor="middle"
            style={{
              fontFamily: 'monospace',
              fontSize: '9px',
              fill: '#64748b',
              letterSpacing: '0.1em',
              userSelect: 'none',
            }}
          >
            {z.label}
          </text>
        </g>
      ))}

      {/* ── Non-attack background edges ───────────────────────────────────── */}
      {EDGES.filter(e => !e.hot).map((e, i) => {
        const n1 = nodeById(e.from);
        const n2 = nodeById(e.to);
        return (
          <line key={i}
            x1={n1.cx} y1={n1.cy} x2={n2.cx} y2={n2.cy}
            stroke="#1e293b" strokeWidth="1" />
        );
      })}

      {/* ── Attack path edges (draw-in animation) ─────────────────────────── */}
      {hotEdges.map((e, i) => {
        const n1 = nodeById(e.from);
        const n2 = nodeById(e.to);
        const len = edgeLen(n1, n2);
        const p   = PHASE[e.phase];
        return (
          <line key={i}
            x1={n1.cx} y1={n1.cy} x2={n2.cx} y2={n2.cy}
            stroke="#ef444488" strokeWidth="2"
            strokeDasharray={`${len} ${len}`}
            style={{
              strokeDashoffset: len,
              animation: `${animName(e.from, e.to)} ${p.dur}ms linear ${p.delay}ms forwards`,
            }}
          />
        );
      })}

      {/* ── Traveling attack dot per hot edge ─────────────────────────────── */}
      {hotEdges.map((e, i) => {
        const n1 = nodeById(e.from);
        const n2 = nodeById(e.to);
        const p  = PHASE[e.phase];
        return (
          <circle key={i} r={5} fill="#ef4444" opacity="0">
            <animate
              attributeName="opacity"
              values="0;1;1;0" keyTimes="0;0.06;0.8;1"
              dur={`${p.dur}ms`} begin={`${p.delay}ms`} fill="freeze"
            />
            <animateMotion
              path={`M${n1.cx},${n1.cy} L${n2.cx},${n2.cy}`}
              dur={`${p.dur}ms`} begin={`${p.delay}ms`} fill="freeze"
            />
          </circle>
        );
      })}

      {/* ── Nodes ─────────────────────────────────────────────────────────── */}
      {NODE_DEFS.map(node => {
        const state  = states[node.id];
        const color  = STATE_COLOR[state] ?? '#475569';
        const glowing = state === 'attacker' || state === 'compromised';
        return (
          <g key={node.id} transform={`translate(${node.cx},${node.cy})`}>
            {/* Glow ring for attacker and compromised nodes */}
            {glowing && (
              <circle r={R + 6} fill="none" stroke="#ef444420" strokeWidth="4" />
            )}
            {/* Main circle */}
            <circle
              r={R}
              fill={color + '18'}
              stroke={color}
              strokeWidth={glowing ? 2 : 1.5}
              style={{ transition: 'stroke 0.6s ease, fill 0.6s ease' }}
            />
            {/* Type icon */}
            <NodeIcon type={node.type} r={R} color={color} />
            {/* Label below node */}
            <text
              textAnchor="middle"
              y={R + 13}
              style={{
                fontFamily: 'monospace',
                fontSize: '8px',
                fill: color,
                userSelect: 'none',
                transition: 'fill 0.6s ease',
              }}
            >
              {node.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
